import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { 
    Globe, Cpu, FileCheck, ArrowRight, 
    CheckCircle, Zap
} from 'lucide-react';
import SEO, { SEO_CONFIG } from '../components/SEO';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

export default function HowItWorksPage({ onRunAudit, onNavigate }) {
    const seo = SEO_CONFIG.howItWorks;
    const steps = [
        {
            number: "01",
            icon: Globe,
            title: "Enter Your Website URL",
            description: "Paste your homepage URL into the audit tool. No signup required — just enter your website address and click analyze.",
            color: "cyan"
        },
        {
            number: "02",
            icon: Cpu,
            title: "AI Analyzes Your Site",
            description: "Our system reviews SEO structure, performance signals, mobile responsiveness, and user experience indicators in seconds.",
            color: "magenta"
        },
        {
            number: "03",
            icon: FileCheck,
            title: "Get Instant Recommendations",
            description: "Receive a clear letter grade plus prioritized improvement suggestions you can act on immediately.",
            color: "green"
        }
    ];

    const colorClasses = {
        cyan: { bg: "from-cyan-500/20 to-blue-500/20", border: "border-cyan-500/30", text: "text-cyan-400", gradient: "from-cyan-400 to-blue-400" },
        magenta: { bg: "from-pink-500/20 to-purple-500/20", border: "border-magenta/30", text: "text-magenta", gradient: "from-pink-400 to-purple-400" },
        green: { bg: "from-green-500/20 to-emerald-500/20", border: "border-green-500/30", text: "text-green-400", gradient: "from-green-400 to-emerald-400" }
    };

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO title={seo.title} description={seo.description} keywords={seo.keywords} />
            
            <Navbar activePage="how-it-works" onNavigate={onNavigate} onRunAudit={onRunAudit} />

            {/* Hero Section */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6">
                        Get Your Website Grade in{' '}
                        <span className="bg-gradient-to-r from-cyan-400 to-magenta bg-clip-text text-transparent">
                            3 Simple Steps
                        </span>
                    </h1>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        No technical knowledge required. Just enter your URL and let our AI do the heavy lifting.
                    </p>
                </div>
            </section>

            {/* Steps Section */}
            <section className="py-16 px-4">
                <div className="max-w-5xl mx-auto">
                    <div className="relative">
                        {/* Connecting Line */}
                        <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-500/50 via-magenta/50 to-green-500/50 transform -translate-x-1/2" />
                        
                        <div className="space-y-16 md:space-y-24">
                            {steps.map((step, index) => {
                                const colors = colorClasses[step.color];
                                const Icon = step.icon;
                                const isEven = index % 2 === 0;
                                
                                return (
                                    <div 
                                        key={step.number}
                                        className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-8 md:gap-16`}
                                    >
                                        {/* Content */}
                                        <div className={`flex-1 ${isEven ? 'md:text-right' : 'md:text-left'}`}>
                                            <div className={`inline-block text-6xl font-bold bg-gradient-to-r ${colors.gradient} bg-clip-text text-transparent mb-4`}>
                                                {step.number}
                                            </div>
                                            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                                                {step.title}
                                            </h2>
                                            <p className="text-gray-400 text-lg">
                                                {step.description}
                                            </p>
                                        </div>
                                        
                                        {/* Icon Circle */}
                                        <div className="relative z-10">
                                            <div className={`w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br ${colors.bg} ${colors.border} border-2 flex items-center justify-center shadow-lg shadow-black/50`}>
                                                <Icon className={`h-10 w-10 md:h-14 md:w-14 ${colors.text}`} />
                                            </div>
                                        </div>
                                        
                                        {/* Spacer for layout */}
                                        <div className="flex-1 hidden md:block" />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* What You Get Section */}
            <section className="py-20 px-4 bg-gradient-to-b from-transparent to-white/5">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                        What You'll Get
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[
                            "Overall website health score (A-F grade)",
                            "SEO structure analysis",
                            "Performance & speed insights",
                            "Mobile optimization feedback",
                            "Conversion & UX recommendations",
                            "Priority-ranked action items"
                        ].map((item, i) => (
                            <div key={i} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg p-4">
                                <CheckCircle className="h-5 w-5 text-emerald-400 shrink-0" />
                                <span className="text-gray-300">{item}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4">
                <div className="max-w-3xl mx-auto text-center">
                    <Card className="bg-gradient-to-br from-magenta/10 to-cyan-500/10 border-magenta/30">
                        <CardContent className="p-10 md:p-16">
                            <Zap className="h-12 w-12 text-magenta mx-auto mb-6" />
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                Ready to Improve Your Website?
                            </h2>
                            <p className="text-gray-400 mb-8 text-lg">
                                Join thousands of businesses who have already improved their online presence with SiteGradePro.
                            </p>
                            <Button 
                                onClick={onRunAudit}
                                size="lg"
                                className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-10 py-6 text-lg"
                            >
                                Analyze My Website Now
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Footer */}
            <Footer onNavigate={onNavigate} />
        </div>
    );
}
